const _excluded = ["disabled", "checked", "defaultChecked", "label", "onChange", "name", "value", "required"];
function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
function _objectWithoutProperties(e, t) { if (null == e) return {}; var o, r, i = _objectWithoutPropertiesLoose(e, t); if (Object.getOwnPropertySymbols) { var n = Object.getOwnPropertySymbols(e); for (r = 0; r < n.length; r++) o = n[r], t.indexOf(o) >= 0 || {}.propertyIsEnumerable.call(e, o) && (i[o] = e[o]); } return i; }
function _objectWithoutPropertiesLoose(r, e) { if (null == r) return {}; var t = {}; for (var n in r) if ({}.hasOwnProperty.call(r, n)) { if (e.indexOf(n) >= 0) continue; t[n] = r[n]; } return t; }
import * as React from "react";
import { RadioGroupContext } from "./RadioGroup";
import { cx } from "../../helpers/utils/cx";
function Radio(_ref, ref) {
  let {
      disabled = false,
      checked,
      defaultChecked,
      label,
      onChange: onChangeProp,
      name: nameProp,
      value: valueProp,
      required
    } = _ref,
    props = _objectWithoutProperties(_ref, _excluded);
  // If we are inside a RadioGroup, get some props from that context
  const group = React.useContext(RadioGroupContext);
  const name = group?.name ?? nameProp; // group name overrides local name.
  const isDisabled = disabled || group?.disabled;
  const isRequired = required || group?.required;
  const isError = group?.error;

  /*
      Scenarios:
       1. Radio is not used inside RadioGroup but is controlled by the user
          - "checked" prop is assumed to be true/false and is passed to the input,
          - "defaultChecked" should not be passed, React will complain.
          - "onChange" is directly passed to the input
       2. Radio is not used inside RadioGroup but is uncontrolled
          - "checked" is either not provided or explicitly undefined
          - "defaultChecked" is passed to the input,
          - "onChange" is directly passed to the input
       3. RadioGroup is used and is controlled by the user (value is provided to RadioGroup)
          - checked value is derived from the group value and the value of the current Radio
          - "defaultChecked" should not be passed, React will complain.
          - "onChange" is passed to the input, but it is wrapped in a function that calls the group onChange
       4. RadioGroup is used and is uncontrolled (value is not provided to RadioGroup)
          - value coming from the group is undefined, comparing that to the value of the current Radio will always return false
          - "defaultValue" from group is compared to the value of the current Radio -> defaultChecked value is derived
          - "onChange" is passed to the input, but it is wrapped in a function that calls the group onChange
      
      Note: we do not handle nested RadioGroups
  */

  let isChecked = checked;
  let isDefaultChecked = defaultChecked;
  if (group && group.value && valueProp != null) {
    isChecked = group.value === valueProp;
  }
  if (group && !group.value && group.defaultValue) {
    isDefaultChecked = group.defaultValue === valueProp;
  }
  let onChange = onChangeProp;
  if (group?.onChange && valueProp != null) {
    onChange = event => {
      group.onChange?.(event);
      onChangeProp?.(event);
    };
  }
  const inputElement = /*#__PURE__*/React.createElement("input", _extends({
    type: "radio"
  }, props, {
    name: name,
    defaultChecked: isDefaultChecked,
    checked: isChecked,
    disabled: isDisabled,
    onChange: onChange,
    value: valueProp,
    ref: ref,
    required: isRequired,
    className: cx( /* shape */"appearance-none m-0 w-6 h-6 rounded-full shrink-0", /* cursor */"cursor-pointer disabled:cursor-not-allowed", /* unchecked */"bg-special-nulled border-solid border-2", /* unchecked border color */isError ? "border-danger-primary" : "border-neutral-primary", /* checked */"checked:bg-static-key-light checked:border-[7px]", /* checked border color */!isError && "checked:border-action-primary", /* disabled */"disabled:bg-special-disabled disabled:border-0", /* disabled + checked */"disabled:checked:border-[7px] disabled:checked:border-separator disabled:checked:bg-special-nulled",
    // this border-separator usage is not safe
    /* hover */
    !isDisabled && (isError ? "hover:border-active-danger-primary" : "hover:border-active-neutral-primary hover:checked:border-active-action-primary"), /* animate */"ease-linear duration-150")
  }));
  if (label) {
    return (
      /*#__PURE__*/
      // eslint-disable-next-line jsx-a11y/label-has-associated-control -- guaranteed to have control element
      React.createElement("label", {
        className: cx("bolt-font-body-m-regular inline-flex gap-2", isError ? "text-danger-primary" : "text-primary", !isDisabled && "cursor-pointer")
      }, inputElement, label)
    );
  }
  return inputElement;
}
const ForwardedRadio = /*#__PURE__*/React.forwardRef(Radio);
export { ForwardedRadio as Radio };